

import React, { useState, useEffect, useCallback } from 'react';
import toast from 'react-hot-toast';
// This assumes the file lives at src/components/POS/DayClose/ShiftReportModal.jsx
// (matching the import in DayClosePage.jsx) with services/config at src/services,src/config
import dayCloseService from '../../../services/dayCloseService';
import printService from '../../../services/printService';
import branchService from '../../../services/branchService';
import { DEFAULT_PRINTER_CONFIG, PRINT_SETTINGS } from '../../../config/printerConfig';
import {
  X,
  Printer,
  RefreshCw,
  Loader2,
  AlertTriangle,
} from 'lucide-react';

const PERIODS = [
  { key: 'today', label: 'Today' },
  { key: 'yesterday', label: 'Yesterday' },
  { key: 'last7Days', label: '7 Days' },
  { key: 'last30Days', label: '30 Days' },
  { key: 'custom', label: 'Custom' },
];

function fmtCurrency(amount) {
  const v = Number(amount || 0);
  const abs = Math.abs(v).toLocaleString('en-IN', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  return v < 0 ? `-₹${abs}` : `₹${abs}`;
}

function fmtDisplayDate(d) {
  return d.toLocaleDateString('en-IN', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

function periodDateRangeLabel(period, customStart, customEnd) {
  const now = new Date();
  const daysAgo = (n) => {
    const d = new Date(now);
    d.setDate(d.getDate() - n);
    return d;
  };
  switch (period) {
    case 'today':
      return fmtDisplayDate(now);
    case 'yesterday':
      return fmtDisplayDate(daysAgo(1));
    case 'last7Days':
      return `${fmtDisplayDate(daysAgo(6))} to ${fmtDisplayDate(now)}`;
    case 'last30Days':
      return `${fmtDisplayDate(daysAgo(29))} to ${fmtDisplayDate(now)}`;
    case 'custom':
      if (customStart && customEnd) {
        if (customStart === customEnd) return fmtDisplayDate(new Date(customStart));
        return `${fmtDisplayDate(new Date(customStart))} to ${fmtDisplayDate(new Date(customEnd))}`;
      }
      return fmtDisplayDate(now);
    default:
      return fmtDisplayDate(now);
  }
}

const PAYMENT_COLORS = {
  CASH: '#16A34A',
  UPI: '#7C3AED',
  CARD: '#2563EB',
  NET_BANKING: '#0891B2',
};

function paymentColor(mode) {
  return PAYMENT_COLORS[(mode || '').toUpperCase()] || '#6B7280';
}

// Handles three possible shapes coming back from dayCloseService, since
// apiClient sometimes returns the full axios response and sometimes an
// interceptor already unwraps it:
//   1. full axios response → res.data = payload
//   2. generic API wrapper → res.data.data = payload (or res.data = payload)
//   3. already-unwrapped    → res = payload directly
function unwrapData(res) {
  const d = res && res.data !== undefined ? res.data : res;
  if (d && typeof d === 'object' && !Array.isArray(d) && 'data' in d) {
    return d.data;
  }
  return d;
}

// ── Plain-text ESC/POS-style content, same approach as ReceiptConfigurations'
//    generateFormattedContent — a monospace text block sent as-is to the
//    Print Agent / server print endpoint. Mirrors the Flutter
//    PrinterService.printCashierReport layout (REVENUE / PAYMENTS / TAX / SUMMARY).
function generateShiftReportContent({
  restaurantName,
  periodLabel,
  snapshot,
  payments,
  totalCollected,
  totalOrders,
  paperWidth = '80mm',
}) {
  const width = paperWidth === '58mm' ? 32 : 48;
  const line = '='.repeat(width);
  const dashed = '-'.repeat(width);
  const center = (text) => {
    const padding = Math.max(0, Math.floor((width - text.length) / 2));
    return ' '.repeat(padding) + text;
  };
  const twoCol = (left, right) => {
    const space = Math.max(1, width - left.length - right.length);
    return left + ' '.repeat(space) + right;
  };
  const fmt = (v) => Number(v || 0).toFixed(2);

  const now = new Date();
  const dateStr = now.toLocaleDateString('en-IN');
  const timeStr = now.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true });

  const content = [];

  content.push(center(restaurantName.toUpperCase()));
  content.push('');
  content.push(center('SHIFT REPORT'));
  content.push(center(periodLabel));
  content.push(center(`Printed: ${dateStr} ${timeStr}`));
  content.push(line);

  content.push('REVENUE');
  content.push(dashed);
  content.push(twoCol('Gross Sales', fmt(snapshot.grossSales)));
  if (snapshot.totalDiscount > 0) content.push(twoCol('Discount', `-${fmt(snapshot.totalDiscount)}`));
  if (snapshot.totalNcAmount > 0) content.push(twoCol('NC Amount', `-${fmt(snapshot.totalNcAmount)}`));
  content.push(twoCol('Net Revenue', fmt(snapshot.totalRevenue)));
  content.push(dashed);

  content.push('PAYMENTS');
  content.push(dashed);
  payments.forEach((p) => {
    const label = p.paymentAccountName || p.paymentMode;
    content.push(twoCol(`${label} (${p.transactionCount} txns)`, fmt(p.totalAmount)));
  });
  content.push(twoCol('Total Collected', fmt(totalCollected)));
  content.push(dashed);

  content.push('TAX');
  content.push(dashed);
  content.push(twoCol('CGST', fmt(snapshot.totalTax / 2)));
  content.push(twoCol('SGST', fmt(snapshot.totalTax / 2)));
  content.push(twoCol('Total Tax', fmt(snapshot.totalTax)));
  content.push(dashed);

  content.push('SUMMARY');
  content.push(dashed);
  content.push(twoCol('Total Orders', String(totalOrders)));
  content.push(twoCol('Gross Sales', fmt(snapshot.grossSales)));
  content.push(twoCol('Deductions', `-${fmt(snapshot.totalDiscount + snapshot.totalNcAmount)}`));
  content.push(twoCol('Tax Collected', fmt(snapshot.totalTax)));
  content.push(dashed);
  content.push(twoCol('NET REV', fmt(snapshot.totalRevenue)));
  content.push(dashed);

  content.push('');
  content.push('');
  content.push('');
  content.push('');

  return content.join('\n');
}

function ReportRow({ label, value, bold, valueColor }) {
  return (
    <div className="flex items-center justify-between px-4 py-2.5">
      <span className={`text-[13px] ${bold ? 'font-bold' : 'font-normal'} text-slate-800`}>{label}</span>
      <span
        className={`text-[13px] ${bold ? 'font-bold' : 'font-medium'}`}
        style={{ color: valueColor || undefined }}
      >
        {value}
      </span>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div className="bg-white rounded-xl border border-slate-200">
      <div className="px-4 pt-3.5 pb-2.5">
        <span className="text-[13px] font-bold text-slate-800">{title}</span>
      </div>
      <div className="border-t border-slate-100 divide-y divide-slate-50">{children}</div>
    </div>
  );
}

export default function ShiftReportModal({ isOpen, onClose, restaurantName: restaurantNameProp }) {
  const [period, setPeriod] = useState('today');
  const [customStart, setCustomStart] = useState('');
  const [customEnd, setCustomEnd] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isPrinting, setIsPrinting] = useState(false);

  const [snapshot, setSnapshot] = useState(null);
  const [payments, setPayments] = useState([]);
  const [orders, setOrders] = useState(null);
  const [branchDetails, setBranchDetails] = useState(null);

  const restaurantName = restaurantNameProp || branchDetails?.name || 'Restaurant';

  // Fetch branch details once — same source PrintReceiptModal/ReceiptDisplay use
  useEffect(() => {
    if (!isOpen || branchDetails) return;
    branchService.getCurrentBranch()
      .then((branch) => {
        if (branch) setBranchDetails(branchService.formatBranchForReceipt(branch));
      })
      .catch(() => {});
  }, [isOpen, branchDetails]);

  const loadData = useCallback(async () => {
    if (period === 'custom' && (!customStart || !customEnd)) return;
    setLoading(true);
    setError(null);
    try {
      const [snapRes, payRes, ordRes] = await Promise.all([
        dayCloseService.getShiftReportFinancialSnapshot(period, customStart, customEnd),
        dayCloseService.getShiftReportPaymentCollections(period, customStart, customEnd),
        dayCloseService.getShiftReportOrdersSummary(period, customStart, customEnd),
      ]);

      const snapData = unwrapData(snapRes) || {};
      const payDataRaw = unwrapData(payRes) || [];
      const ordData = unwrapData(ordRes) || {};

      setSnapshot({
        totalRevenue: Number(snapData.totalRevenue || 0),
        totalDiscount: Number(snapData.totalDiscount || 0),
        totalNcAmount: Number(snapData.totalNcAmount || 0),
        totalTax: Number(snapData.totalTax || 0),
        grossSales: Number(snapData.grossSales || 0),
      });
      setPayments(
        (Array.isArray(payDataRaw) ? payDataRaw : []).map((p) => ({
          paymentAccountName: p.paymentAccountName || p.accountName || '',
          paymentMode: p.paymentMode || p.mode || '',
          totalAmount: Number(p.totalAmount ?? p.amount ?? 0),
          transactionCount: Number(p.transactionCount ?? p.count ?? 0),
        }))
      );
      setOrders({ currentOrders: Number(ordData.currentOrders || 0) });
    } catch (e) {
      setError(e?.response?.data?.message || 'Failed to load shift report');
    } finally {
      setLoading(false);
    }
  }, [period, customStart, customEnd]);

  useEffect(() => {
    if (!isOpen) return;
    if (period !== 'custom') loadData();
  }, [isOpen, period, loadData]);

  useEffect(() => {
    if (!isOpen) return;
    if (period === 'custom' && customStart && customEnd) loadData();
  }, [isOpen, period, customStart, customEnd, loadData]);

  if (!isOpen) return null;

  const totalCollected = payments.reduce((sum, p) => sum + p.totalAmount, 0);
  const totalOrders = orders?.currentOrders ?? 0;
  const periodLabel = periodDateRangeLabel(period, customStart, customEnd);

  // Browser print fallback — same shape as PrintReceiptModal/ReceiptDisplay's
  // handleThermalPrint: open a window, dump preformatted monospace text, print.
  const handleBrowserPrintFallback = (content) => {
    const printWindow = window.open('', '', 'width=800,height=600');
    if (!printWindow) {
      toast.error('Popup blocked! Please allow popups for this site.');
      return;
    }
    const config = DEFAULT_PRINTER_CONFIG;
    const html = `
      <!DOCTYPE html>
      <html>
        <head>
          <meta charset="UTF-8">
          <title>Shift Report</title>
          <style>
            * { margin: 0; padding: 0; box-sizing: border-box; }
            @media screen {
              body { font-family: 'Courier New', monospace; background: #f5f5f5; padding: 20px; display: flex; justify-content: center; }
              .receipt-container { background: white; width: ${config.width}; padding: ${config.padding}; box-shadow: 0 2px 10px rgba(0,0,0,0.1); border: 1px solid #ddd; }
            }
            @media print {
              @page { size: ${config.width} auto; margin: 0; }
              body { margin: 0; padding: 0; width: ${config.width}; }
              .receipt-container { width: 100%; padding: ${config.padding}; }
            }
            .receipt-container { font-family: 'Courier New', monospace; font-size: ${config.fontSize}; line-height: ${config.lineHeight}; color: #000; white-space: pre-wrap; }
          </style>
        </head>
        <body>
          <div class="receipt-container">${content.replace(/</g, '&lt;').replace(/>/g, '&gt;')}</div>
          <script>
            window.onload = function() { setTimeout(function() { window.print(); }, ${PRINT_SETTINGS.autoPrintDelay}); };
            ${PRINT_SETTINGS.autoCloseAfterPrint ? `window.onafterprint = function() { setTimeout(function() { window.close(); }, ${PRINT_SETTINGS.autoCloseDelay}); };` : ''}
          </script>
        </body>
      </html>
    `;
    printWindow.document.write(html);
    printWindow.document.close();
  };

  const handlePrint = async () => {
    if (!snapshot) return;
    setIsPrinting(true);
    try {
      const content = generateShiftReportContent({
        restaurantName,
        periodLabel,
        snapshot,
        payments,
        totalCollected,
        totalOrders,
      });

      const isPrintAgentAvailable = await printService.isPrintAgentAvailable();

      if (isPrintAgentAvailable) {
        // Same call shape as ReceiptConfigurations.jsx's Print Agent path
        const response = await fetch(`http://localhost:${printService.getPrintAgentPort()}/print/bill`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ content, printerId: null }),
        });
        const result = await response.json();
        if (result?.success) {
          toast.success('Shift report sent to printer!');
        } else {
          toast.error(result?.error || result?.message || 'Print failed — falling back to browser print');
          handleBrowserPrintFallback(content);
        }
      } else {
        // Fallback to server-side print, same as testPrintTemplate calls elsewhere
        const result = await printService.testPrintTemplate(content, 'BILL', null);
        if (result?.success) {
          toast.success('Shift report printed!');
        } else {
          handleBrowserPrintFallback(content);
        }
      }
    } catch (err) {
      console.error('Shift report print error:', err);
      // Any unexpected failure still gets the browser fallback so the user isn't stuck
      try {
        const content = generateShiftReportContent({
          restaurantName, periodLabel, snapshot, payments, totalCollected, totalOrders,
        });
        handleBrowserPrintFallback(content);
      } catch {
        toast.error('Failed to print shift report');
      }
    } finally {
      setIsPrinting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl mx-4 max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between">
          <h3 className="text-lg font-bold text-slate-800">Shift Report</h3>
          <div className="flex items-center gap-2">
            <button
              onClick={loadData}
              disabled={loading}
              className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg transition disabled:opacity-50"
              title="Refresh"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
            </button>
            <button
              onClick={handlePrint}
              disabled={loading || isPrinting || !snapshot}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-white bg-teal-600 hover:bg-teal-700 rounded-lg transition disabled:opacity-50"
            >
              {isPrinting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Printer className="w-3.5 h-3.5" />}
              {isPrinting ? 'Printing...' : 'Print'}
            </button>
            <button onClick={onClose} className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg transition">
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Period filter */}
        <div className="px-6 py-3 border-b border-slate-100 flex flex-wrap items-center gap-2">
          {PERIODS.map((p) => (
            <button
              key={p.key}
              onClick={() => setPeriod(p.key)}
              className={`px-3.5 py-1.5 rounded-full text-xs font-medium transition border ${
                period === p.key
                  ? 'bg-teal-600 text-white border-teal-600'
                  : 'bg-white text-slate-500 border-slate-200 hover:text-slate-700'
              }`}
            >
              {p.label}
            </button>
          ))}
          {period === 'custom' && (
            <div className="flex items-center gap-2 ml-1">
              <input
                type="date"
                value={customStart}
                onChange={(e) => setCustomStart(e.target.value)}
                className="border border-slate-200 rounded-lg px-2 py-1 text-xs"
              />
              <span className="text-xs text-slate-400">to</span>
              <input
                type="date"
                value={customEnd}
                onChange={(e) => setCustomEnd(e.target.value)}
                className="border border-slate-200 rounded-lg px-2 py-1 text-xs"
              />
            </div>
          )}
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-6 py-5">
          {loading && !snapshot ? (
            <div className="flex items-center justify-center py-20">
              <Loader2 className="w-8 h-8 text-teal-500 animate-spin" />
            </div>
          ) : error ? (
            <div className="flex flex-col items-center justify-center py-20 text-center">
              <AlertTriangle className="w-10 h-10 text-red-400 mb-3" />
              <p className="text-sm text-slate-600 mb-4">{error}</p>
              <button onClick={loadData} className="px-4 py-2 bg-teal-600 text-white text-sm font-medium rounded-lg hover:bg-teal-700 transition">
                Retry
              </button>
            </div>
          ) : !snapshot ? (
            <div className="py-20 text-center text-sm text-slate-400">Select a date range to load the report.</div>
          ) : (
            <div className="space-y-4">
              {/* Header gradient card */}
              <div
                className="rounded-2xl p-5 text-white"
                style={{ background: 'linear-gradient(135deg, #1E3A5F 0%, #0D2137 100%)' }}
              >
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-2">
                    <span className="font-bold text-sm">{restaurantName}</span>
                  </div>
                  <span className="px-2.5 py-1 rounded-full bg-white/15 text-xs font-medium">{periodLabel}</span>
                </div>
                <p className="text-xs text-white/70">Net Revenue</p>
                <p className="text-3xl font-bold mt-1">{fmtCurrency(snapshot.totalRevenue)}</p>
                <p className="text-xs text-white/60 mt-1">{totalOrders} orders</p>
              </div>

              {/* Revenue Breakdown */}
              <Section title="Revenue Breakdown">
                <ReportRow label="Gross Sales" value={fmtCurrency(snapshot.grossSales)} />
                <ReportRow label="Discount" value={fmtCurrency(-snapshot.totalDiscount)} valueColor="#DC2626" />
                {snapshot.totalNcAmount > 0 && (
                  <ReportRow label="NC Amount" value={fmtCurrency(-snapshot.totalNcAmount)} valueColor="#DC2626" />
                )}
                <ReportRow label="Net Revenue" value={fmtCurrency(snapshot.totalRevenue)} bold />
              </Section>

              {/* Payment Collections */}
              <Section title="Payment Collections">
                {payments.map((p, i) => {
                  const label = p.paymentAccountName || p.paymentMode;
                  return (
                    <ReportRow
                      key={`${p.paymentMode}-${i}`}
                      label={label}
                      value={`${fmtCurrency(p.totalAmount)}  (${p.transactionCount} txns)`}
                      valueColor={paymentColor(p.paymentMode)}
                    />
                  );
                })}
                <ReportRow label="Total Collected" value={fmtCurrency(totalCollected)} bold />
              </Section>

              {/* Tax */}
              <Section title="Tax Collected">
                <ReportRow label="CGST" value={fmtCurrency(snapshot.totalTax / 2)} />
                <ReportRow label="SGST" value={fmtCurrency(snapshot.totalTax / 2)} />
                <ReportRow label="Total Tax" value={fmtCurrency(snapshot.totalTax)} bold />
              </Section>

              {/* Summary */}
              <Section title="Summary">
                <ReportRow label="Total Orders" value={String(totalOrders)} />
                <ReportRow label="Gross Sales" value={fmtCurrency(snapshot.grossSales)} />
                <ReportRow label="Discounts" value={fmtCurrency(snapshot.totalDiscount)} />
                <ReportRow label="NC Amount" value={fmtCurrency(snapshot.totalNcAmount)} />
                <ReportRow label="Tax Collected" value={fmtCurrency(snapshot.totalTax)} />
                <ReportRow label="Net Revenue" value={fmtCurrency(snapshot.totalRevenue)} bold valueColor="#0d9488" />
              </Section>              
            </div>
          )}
        </div>
      </div>
    </div>
  );
}